import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import numberUtils from 'shared/src/utils/number'
import settings from 'shared/src/settings'
import info from 'shared/src/resource/info'
import {
  getUrl,
} from 'utils/storage'
pdfMake.vfs = pdfFonts.pdfMake.vfs

const PDFQuote = ({
  quote,
  client,
  project,
  calculated,
  clientView,
  currency,
  inputMarkups,
  inputVat,
  activeProjectId,
  token,
}) => {

  const {
    rows,
    totals,
    markups,
  } = calculated

  const images = {}

  const company = project.meta && project.meta.company ?
    project.meta.company :
    {}

  const tableLayouts = {
    quoteTable: {
      hLineWidth: (i, node) => {
        if (i === 0 || i === node.table.body.length) {
          return 0;
        }
        return (i === node.table.headerRows) ? 2 : 1;
      },
      vLineWidth: (i) => {
        return 0;
      },
      hLineColor: (i) => {
        return i === 1 ? 'black' : '#aaa';
      },
      paddingLeft: (i) => {
        return i === 0 ? 0 : 8;
      },
      paddingRight: (i, node) => {
        return (i === node.table.widths.length - 1) ? 0 : 8;
      }
    }
  }

  const getAmount = (row) => {
    const {
      basePrice,
      price,
      priceMode,
      item,
    } = row

    if(item.type == 'folder') return ''
    if(priceMode == 'priceBased' && basePrice) {
      return basePrice.amount
    }
    else {
      return numberUtils.twoDp(price.amount)
    }
  }

  const getUnit = (row) => {
    const {
      basePrice,
      price,
      priceMode,
      item,
    } = row
    if(item.type == 'folder') return ''
    return priceMode == 'priceBased' && basePrice ?
      basePrice.unit :
      price.unit
  }

  const getAdjustment = (row) => {
    const {
      item,
      basePrice,
      priceMode,
    } = row
    if(item.type == 'folder') return ''
    if(item.resource.type == 'template') return ''
    if(priceMode == 'priceBased' && basePrice) return ''
    const inputs = item.inputs || {}
    return inputs.adjustment || ''
  }

  const getPrice = (row) => {
    const {
      item,     
      price,
    } = row
    if(item.type == 'folder') return ''
    if(item.resource.type == 'template') return ''
    const field = 'price'
    const calculatedValue = price.base.unitPrice || price.base.price
    const inputs = item.inputs || {}
    const edits = item.edits || {}
    let value = ''
    if(edits[field]) {
      value = inputs[field]
    }
    else {
      value = inputs[field] || calculatedValue
    }
    return `${currency}${numberUtils.twoDp(value, true)}`
  }

  const getTotal = (row) => {
    const {
      price,
      expanded,
      hasChildren,
    } = row
    if(expanded && hasChildren) return ''
    return `${currency}${numberUtils.twoDp(price.price, true)}`
  }

  const getPriceValue = (value) => `${currency}${numberUtils.twoDp(value, true)}`

  const getTotalRow = ({
    title,
    value,
    amount,
    price,
    bold = false,
  }) => {
    const cells = [{
      text: title,
      alignment: 'right',
      style: bold ? 'titleCellTotalBold' : 'titleCellTop',
    }]
    cells.push(amount ? {
      text: amount,
      alignment: 'right',
      style: 'dataCell',
    } : '')
    cells.push('')
    if(!clientView) cells.push('')
    cells.push(price ? {
      text: getPriceValue(price),
      alignment: 'right',
      style: 'dataCell',
    } : '')
    cells.push({
      text: getPriceValue(value),
      alignment: 'right',
      style: bold ? 'dataCellBold' : 'dataCell',
    })
    return cells
  }

  const columns = [{
    text: 'Name',
    alignment: 'left',
    style: 'headerCell',
  },{
    text: 'Amount',
    alignment: 'right',
    style: 'headerCell',
  },{
    text: 'Unit',
    alignment: 'left',
    style: 'headerCell',
  }, clientView ? null : {
    text: '+/-',
    alignment: 'right',
    style: 'headerCell',
  },{
    text: 'Price',
    alignment: 'right',
    style: 'headerCell',
  },{
    text: 'Total',
    alignment: 'right',
    style: 'headerCell',
  }].filter(c => c)

  const dataRows = rows.map(row => {
    let prefix = ''
    let padding = ''
    let nameStyle = ''
    for(let i=0; i < row.depth; i++) {
      prefix += '-'
      padding += '        '
    }
    if(row.depth == 0) {
      nameStyle = 'titleCellTop'
    }
    else if(row.depth == 1) {
      nameStyle = 'titleCellMiddle'
    }
    else {
      nameStyle = 'titleCellBottom'
    }
    return [{
      text: `${prefix}${padding}${row.item.resource.name}`,
      alignment: 'left',
      style: nameStyle,
    },{
      text: getAmount(row),
      alignment: 'right',
      style: 'dataCell',
    },{
      text: getUnit(row),
      alignment: 'left',
      style: 'dataCell',
    }, clientView ? null : {
      text: getAdjustment(row),
      alignment: 'right',
      style: 'dataCell',
    },{
      text: getPrice(row),
      alignment: 'right',
      style: 'dataCell',
    }, {
      text: getTotal(row),
      alignment: 'right',
      style: 'dataCell',
    }].filter(c => c)
  })

  const totalRows = []

  if(!clientView) {
    totalRows.push(getTotalRow({
      title: `Costs Total`,
      value: totals.costsTotal,
      bold: true,
    }))
    
    settings.markupOrder.map((field, i) => {

      const markup = markups[field]
      const inputField = inputMarkups[field]

      totalRows.push(getTotalRow({
        title: `% ${info.markupTitle(field)} Markup`,
        value: markup.total,
        amount: inputField.value,
        price: markup.price,
      }))
    })

    totalRows.push(getTotalRow({
      title: `Markup Total`,
      value: totals.markupTotal,
      bold: true,
    }))
  }

  totalRows.push(getTotalRow({
    title: `Sub Total`,
    value: totals.subTotal,
    bold: true,
  }))

  totalRows.push(getTotalRow({
    title: `% VAT`,
    amount: inputVat.value,
    value: totals.vatAmount,
    price: totals.subTotal,
  }))

  totalRows.push(getTotalRow({
    title: `Grand Total`,
    value: totals.grandTotal,
    bold: true,
  }))

  const body = [
    columns
  ].concat(dataRows).concat(totalRows)

  if(company.logo) {
    images.logo = `${location.origin}${getUrl(activeProjectId, company.logo.filepath, token)}`
  }
  
  const definition = {
    content: [
      {
        columns: [
          [{
            text: quote.name,
            style: 'headerTitle',
          },{
            text: quote.meta.type,
            style: 'headerCaption'
          }],
          company.logo ?
            {
              image: 'logo',
              width: 80,
              alignment: 'right',
            } : ''
        ]
      },
      {
        columns: [
          [{
            text: client.name,
            style: 'headerData'
          }].concat([
            'email',
            'address1',
            'address2',
            'city',
            'county',
            'postcode',
          ].filter(field => client.meta[field]).map(field => {
            return {
              text: client.meta[field],
              style: 'headerCaption',
            }
          })),
          [{
            text: company.name,
            style: 'headerData',
            alignment: 'right',
          },{
            text: `${ company.email } : ${ company.phone }`,
            style: 'headerCaption',
            alignment: 'right',
          }].concat((company.address || '').split(/\n+/).map((line, i) => {
            return {
              text: line,
              style: 'headerCaption',
              alignment: 'right',
            }
          }))
        ]
      },
      {
        style: 'quoteTable',
        layout: 'quoteTable',
        table: {
          headerRows: 1,
          widths: [ '*', 'auto', 'auto', clientView ? '' : 'auto', 'auto', 'auto' ].filter(w => w),
          // dontBreakRows: true,
          // keepWithHeaderRows: 1,
          body,
        }
      },
    ],
    styles: {
      headerTitle: {
        fontSize: 13,
        color: '#000000',
        margin: [0, 0, 0, 5]
      },
      headerData: {
        fontSize: 11,
        color: '#000000',
        margin: [0, 0, 0, 5]
      },
      headerCaption: {
        fontSize: 10,
        color: '#666666',
        margin: [0, 0, 0, 5]
      },
      quoteTable: {
        margin: [0, 5, 0, 15],
      },
      headerCell: {
        fontSize: 11,
        bold: true,
        color: '#000000',
        margin: [0, 0, 0, 15]
      },
      dataCell: {
        fontSize: 9,
        color: '#333333',
        margin: [5, 5, 5, 5],
      },
      dataCellBold: {
        fontSize: 9,
        bold: true,
        color: '#333333',
        margin: [5, 5, 5, 5],
      },
      titleCellTop: {
        fontSize: 10,
        color: '#333333',
        margin: [5, 5, 5, 5],
      },
      titleCellMiddle: {
        fontSize: 10,
        color: '#666666',
        margin: [5, 5, 5, 5],
      },
      titleCellBottom: {
        fontSize: 9,
        color: '#999999',
        margin: [5, 5, 5, 5],
      },
      titleCellTotalBold: {
        fontSize: 10,
        bold: true,
        color: '#333333',
        margin: [5, 5, 5, 5],
      },
      
    },
    defaultStyle: {
      columnGap: 20
    },
    images,
  }
  const win = window.open('', '_blank')
  win.document.body.innerHTML = `
<style>
  .container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .container > div {
    color: #999;
    font-size: 20px;
    font-family: Arial;
    padding: 30px;
    border-radius: 5px;
    border: 1px solid #ccc;
    box-shadow: 5px 5px 5px 0px rgba(0,0,0,0.4);
  }
</style>
<div class="container">
  <div>
    generating your PDF...
  </div>
</div>
`
  pdfMake.createPdf(definition, tableLayouts).open({}, win)
}

export default PDFQuote